/**
 * Reporting timezone for the funnels area (feat/funnel-report-tz).
 *
 * Every analytics day ("Today", "Last 7 days", bucket day keys, displayed
 * timestamps) means a day in THIS zone, regardless of the operator's machine
 * timezone. This is the ONE definition; `DateRangeContext` re-exports it as
 * `SHOP_TZ` rather than declaring a second literal.
 *
 * IT MUST EQUAL THE BACKEND'S ZONE. The backend reads `REPORT_TIMEZONE`
 * (`lb_page_stats_service.REPORT_TZ_NAME`); both sides default to UTC. Day keys
 * travel the API verbatim, so if the two disagree the UI requests day keys the
 * backend never bucketed and every money figure reads zero or wrong for the
 * offset window each night — it fails silently, with no error anywhere.
 *
 * So set `REACT_APP_REPORT_TIMEZONE` here to the SAME IANA zone you set
 * `REPORT_TIMEZONE` to on the backend, in the same change. CRA bakes this at
 * build time, so changing it needs a frontend rebuild as well as a restart.
 */
export const REPORT_TZ = process.env.REACT_APP_REPORT_TIMEZONE || "UTC";

// en-CA gives ISO-shaped YYYY-MM-DD from Intl.
const dayFmt = new Intl.DateTimeFormat("en-CA", { timeZone: REPORT_TZ });

/** Today's YYYY-MM-DD in the reporting timezone. */
export const tzToday = () => dayFmt.format(new Date());

const DAY_MS = 86400000;
const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Shift a reporting-zone day KEY by whole calendar days:
 * `tzShiftDays("2026-03-30", -1)` is `"2026-03-29"`.
 *
 * CALENDAR ARITHMETIC, NEVER INSTANT ARITHMETIC. The arithmetic runs on the day
 * key in UTC — where every day is exactly 86400000 ms — and the key never
 * re-enters a timezone, so a DST transition inside the span cannot move it.
 * Subtracting milliseconds from `Date.now()` and formatting the RESULT in the
 * reporting zone (what `tzDaysAgo` used to do) is off by a full day whenever
 * the lookback crosses a spring-forward boundary: just after midnight on the
 * day a zone loses an hour, "1 day ago" answers two days back, because 24h
 * earlier is still late on the day before that.
 *
 * This is the same day-key shift `quizAnalyticsModel.shiftDays` uses, kept here
 * because `reportTz` is the leaf every funnels surface imports.
 *
 * Anything unparseable comes back untouched — a preset must degrade, not throw.
 */
export const tzShiftDays = (dayKey, days) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey ?? ""));
  const n = Number(days);
  if (!m || !Number.isFinite(n)) return String(dayKey ?? "");
  const t = new Date(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + Math.round(n) * DAY_MS,
  );
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
};

/** The YYYY-MM-DD `n` days before today, in the reporting timezone. */
export const tzDaysAgo = (n) => tzShiftDays(tzToday(), -(Number(n) || 0));

/** Human timestamp (e.g. "12 Jul, 20:41") in the reporting timezone. */
export const tzDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: REPORT_TZ,
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
};
